import React, { useState } from 'react';
import { Search, User, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';

interface HeaderProps {
  title?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearch?: (searchTerm: string) => void;
  actions?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ 
  title,
  showSearch = false,
  searchPlaceholder = "Search",
  onSearch,
  actions
}) => {
  const { user, logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  return (
    <header className="header">
      <div className="header-left">
        {title && (
          <div className="header-title">
            <h1 className="page-title">{title}</h1>
          </div>
        )}
      </div>

      <div className="header-right">
        {showSearch && (
          <div className="header-search">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                className="search-input"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        )}

        {actions && (
          <div className="header-actions">
            {actions}
          </div>
        )}

        {user && (
          <div className="header-user">
            <button 
              className="user-menu-trigger"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">
                <User size={20} />
              </div>
              <div className="user-info">
                <span className="user-name">{user.first_name} {user.last_name}</span>
                <span className="user-role">{user.role}</span>
              </div>
              <ChevronDown size={16} />
            </button>

            {showUserMenu && (
              <div className="user-menu">
                <div className="user-menu-header">
                  <div className="user-details">
                    <span className="user-full-name">{user.first_name} {user.last_name}</span>
                    <span className="user-email">{user.email}</span>
                    <span className="user-role-badge">{user.role}</span>
                  </div>
                </div>
                <div className="user-menu-separator"></div>
                <div className="user-menu-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="logout-button"
                  >
                    <LogOut size={16} />
                    Logout
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div 
          className="overlay" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
};

export default Header;